import React from 'react'
import { Alert as JTAlert } from '@jobtarget/ui-library'

interface ButtonConfig {
  text: string
  variant?: "danger" | "primary" | "danger-outline" | "default" | "primary-outline" | "success" | "success-outline" | "warning" | "warning-outline" | "dark-warning" | "dark-warning-outline"
  onClick: () => void
  icon?: string
  iconType?: 'svg' | 'fontawesome'
}

interface AlertProps {
  variant: "danger" | "primary" | "danger-outline" | "default" | "primary-outline" | "success" | "success-outline" | "warning" | "warning-outline" | "dark-warning" | "dark-warning-outline"
  message: string
  showIcon?: boolean
  buttons?: ButtonConfig[] // Uncomment this line
  onClose: (bool: boolean) => void
}

// reusable alert component
const Alert = ({ variant = "warning", message, showIcon = true, buttons = [], onClose }: AlertProps) => {
    // Limit to max 2 buttons
    const limitedButtons = buttons.slice(0, 2)
    const isMobile = window.innerWidth < 768
  
    const iconSize = isMobile ? '1.5rem' : '24px'
    const padding = isMobile ? '1rem' : '16px'
    const marginRight = isMobile ? '1rem' : '16px'

    // Helper function to convert variant names to Bootstrap classes
    const getBootstrapClass = (variant?: string, index?: number) => {
        if (!variant) {
        return index === 0 ? "btn-primary" : "btn-secondary"
        }
        
        // Check if it's an outline variant
        if (variant.endsWith('-outline')) {
        const baseColor = variant.replace('-outline', '')
        
        // Handle dark-warning specifically
        if (baseColor === 'dark-warning') {
            return 'btn-outline-custom-dark-warning'
        }
        
        return `btn-outline-${baseColor}`
        } else {
        // Handle dark-warning specifically
        if (variant === 'dark-warning') {
            return 'btn-custom-dark-warning'
        }
        
        return `btn-${variant}`
        }
    }
  
  // Also add inline styles for the custom dark warning
  const getCustomStyles = (variant?: string) => {
    if (variant === 'dark-warning') {
      return {
        backgroundColor: '#855700',
        borderColor: '#855700',
        color: 'white'
      }
    }
    if (variant === 'dark-warning-outline') {
      return {
        backgroundColor: 'transparent',
        borderColor: '#855700',
        color: '#855700'
      }
    }
    return {}
  }

  const getJTAlertVariant = (variant: string) => {
    if (variant === 'dark-warning' || variant === 'dark-warning-outline') {
      return 'warning' // Use warning for JTAlert, but keep custom styling for buttons
    }
    return variant as "danger" | "primary" | "danger-outline" | "default" | "primary-outline" | "success" | "success-outline" | "warning" | "warning-outline"
  }

    return (
      <div className="alert-container">
        <JTAlert 
            variant={getJTAlertVariant(variant)} 
            showIcon={false} 
            isOpen={true}
            handleClose={(bool: boolean) => onClose(bool)}
            style={{ 
                position: 'relative',
                width: '100%',
                minHeight: '60px'
            }}
        >
            {/* Position our content absolutely to escape JTAlert's constraints */}
            <div 
                className="d-flex align-items-center" 
                style={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                padding: padding, // Consistent padding
                }}
            >
                {/* Custom info icon */}
                {showIcon && (
                <div style={{ flexShrink: 0, marginRight: marginRight }}>
                    <img 
                    src="/svgs/info.svg" 
                    alt="Info" 
                    style={{ 
                        width: iconSize, 
                        height: iconSize 
                    }}
                    />
                </div>
                )}

                <span style={{ flexGrow: 1, fontSize: isMobile ? '0.7rem' : '0.95rem', lineHeight: '1.4' }}>
                {message}
                </span>
                
                {limitedButtons.length > 0 && (
                <div className="d-flex" style={{ flexShrink: 0, gap: isMobile ? '8px' : '12px', marginLeft: marginRight }}>
                    {limitedButtons.map((button, index) => (
                        <button
                            key={index}
                            className={`btn ${getBootstrapClass(button.variant, index)}`}
                            style={{
                            ...getCustomStyles(button.variant),
                            whiteSpace: 'nowrap',
                            minWidth: isMobile ? '48px' : 'auto',
                            minHeight: isMobile ? '40px' : undefined,
                            width: isMobile ? '40px' : undefined,
                            height: isMobile ? '40px' : undefined,
                            padding: isMobile ? '8px' : undefined, 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            }}
                            onClick={button.onClick}
                        >
                            {isMobile ? (
                                button.icon ? (
                                    button.iconType === 'fontawesome' ? (
                                        <i 
                                            className={button.icon} 
                                            style={{ 
                                                fontSize: '28px',
                                                color: button.variant?.includes('outline') ? '#855700' : 'white'
                                            }}
                                        />
                                    ) : (
                                        <img 
                                            src={button.icon} 
                                            alt={button.text} 
                                            style={{ 
                                                width: '28px',
                                                height: '28px'
                                            }}
                                        />
                                    )
                                ) : button.text
                            ) : (
                                button.text
                            )}
                        </button>
                    ))}
                </div>
                )}
            </div>
        </JTAlert>
      </div>
    )
  }

export default Alert